// Types for Lead Source Rules Editor

export interface SimpleRule {
  type: 'simple';
  column: string;
  operator:
    | 'LIKE'
    | 'NOT LIKE'
    | '='
    | '>'
    | '<'
    | '>='
    | '<='
    | '!='
    | 'IN'
    | 'NOT IN'
    | 'IS NULL'
    | 'IS NOT NULL';
  value: string | number | (string | number)[] | null;
}

export interface ComplexRule {
  type: 'complex';
  operator: 'AND' | 'OR' | 'NOT';
  rules: Rule[];
}

export type Rule = SimpleRule | ComplexRule;

export interface SourceRules {
  [source: string]: Rule[];
}

export interface CustomerRules {
  [customer: string]: SourceRules;
}

export type RuleType = 'lps' | 'vnr';

export type PreviewTimeframe =
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'
  | 'full';

// Timeframe display labels (German)
export const timeframeLabels: Record<PreviewTimeframe, string> = {
  today: 'Heute',
  yesterday: 'Gestern',
  this_week: 'Diese Woche',
  last_week: 'Letzte Woche',
  this_month: 'Dieser Monat',
  last_month: 'Letzter Monat',
  full: 'Alle Daten',
};

export type LeadStatus = 'all' | 'new' | 'updated' | 'duplicate' | 'error' | 'undefined';

// Lead status display labels (German)
export const leadStatusLabels: Record<LeadStatus, string> = {
  all: 'Alle',
  new: 'Neu',
  updated: 'Aktualisiert (Tags/Reaktiviert)',
  duplicate: 'Dubletten',
  error: 'Fehler',
  undefined: 'Unbekannt',
};

export interface RuleVersion {
  id: number;
  version_number: number;
  rule_type: RuleType;
  rules_json?: string;
  rules?: CustomerRules;
  created_by: string;
  created_at: string;
  comment: string | null;
  is_active: boolean;
}

export interface RuleColumn {
  id: number;
  column_name: string;
  display_name: string;
  data_type: 'string' | 'number' | 'null';
  table_source: string;
  available_for: 'lps' | 'vnr' | 'both';
  sample_values: string[] | null;
  description: string | null;
}

export interface PreviewResult {
  count: number;
  query_time_ms: number;
  timeframe: PreviewTimeframe;
}

export interface PreviewLead {
  process_id: number;
  email: string;
  confirmed: string;
  origin_host: string;
  source: string;
  clean_affiliate: string;
  adref: string;
}

export interface DistributionEntry {
  value: string | null;
  count: number;
}

export interface UnbekannteDistribution {
  total: number;
  query_time_ms: number;
  distributions: Record<string, DistributionEntry[]>;
}

// Source display config (from lead_source_display_config table)
export interface SourceDisplayConfig {
  source_key: string;
  display_name: string;
  sort_order: number;
  is_visible: boolean;
  color: string | null;
}

// Customer display names
export const customerNames: Record<string, string> = {
  ft: 'Finanztrends',
  bg: 'Börse Global',
  sw: 'Stock-World',
  tt: 'Trading-Treff',
  be: 'Börse-Express',
  kme: 'Kapitalmarktexperten',
  foreign: 'Foreign Leads',
  non_finance: 'Non-Finance',
};

// Operator options
export const operatorOptions: {
  value: SimpleRule['operator'];
  label: string;
  needsValue: boolean;
}[] = [
  { value: '=', label: '= (gleich)', needsValue: true },
  { value: '!=', label: '!= (ungleich)', needsValue: true },
  { value: 'LIKE', label: 'LIKE (enthält)', needsValue: true },
  { value: 'NOT LIKE', label: 'NOT LIKE (enthält nicht)', needsValue: true },
  { value: '>', label: '> (größer)', needsValue: true },
  { value: '<', label: '< (kleiner)', needsValue: true },
  { value: '>=', label: '>= (größer/gleich)', needsValue: true },
  { value: '<=', label: '<= (kleiner/gleich)', needsValue: true },
  { value: 'IN', label: 'IN (einer von)', needsValue: true },
  { value: 'NOT IN', label: 'NOT IN (keiner von)', needsValue: true },
  { value: 'IS NULL', label: 'IS NULL (leer)', needsValue: false },
  {
    value: 'IS NOT NULL',
    label: 'IS NOT NULL (nicht leer)',
    needsValue: false,
  },
];
